import axios from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clearTokens,
} from '../auth/token';
import { toApiRequestError } from './errors';

export class BaseApiService {
  private static instance: BaseApiService;
  protected axiosInstance: AxiosInstance;

  // Prevents multiple simultaneous refresh calls when several
  // requests 401 at the same time
  private isRefreshing = false;
  private refreshSubscribers: Array<{
    resolve: (token: string) => void;
    reject: (error: AxiosError | Error) => void;
  }> = [];

  public constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
      headers: { 'Content-Type': 'application/json' },
    });
    this.setupInterceptors();
  }

  public static getInstance(): BaseApiService {
    if (!BaseApiService.instance) {
      BaseApiService.instance = new BaseApiService();
    }
    return BaseApiService.instance;
  }

  private onRefreshed(newToken: string): void {
    this.refreshSubscribers.forEach(({ resolve }) => resolve(newToken));
    this.refreshSubscribers = [];
  }

  private onRefreshFailed(error: AxiosError | Error): void {
    this.refreshSubscribers.forEach(({ reject }) => reject(error));
    this.refreshSubscribers = [];
  }

  private redirectToLogin(): void {
    if (window.location.pathname !== '/login') {
      setTimeout(() => { window.location.href = `${import.meta.env.BASE_URL}login`; }, 2000);
    }
  }

  private setupInterceptors(): void {

    // ── Request: attach Bearer token ──────────────────────────
    this.axiosInstance.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });

    // ── Response: silent refresh on 401 ──────────────────────
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retried?: boolean;
          headers: Record<string, string>;
          url?: string;
        };

        const isPublicAuthRequest =
          !!originalRequest.url &&
          (
            originalRequest.url.includes('/login') ||
            originalRequest.url.includes('/verify-otp') ||
            originalRequest.url.includes('/resend-otp')
          );

        if (
          error.response?.status === 401 &&
          !originalRequest._retried &&
          !isPublicAuthRequest
        ) {
          originalRequest._retried = true;

          // If the failing call IS the refresh endpoint, bail out
          if (originalRequest.url?.includes('/refresh')) {
            clearTokens();
            this.redirectToLogin();
            return Promise.reject(toApiRequestError(error, 'Session refresh failed.'));
          }

          // Queue concurrent requests until refresh resolves
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.refreshSubscribers.push({
                resolve: (newToken: string) => {
                originalRequest.headers['Authorization'] =
                  `Bearer ${newToken}`;
                resolve(this.axiosInstance(originalRequest));
                },
                reject,
              });
            });
          }

          this.isRefreshing = true;

          try {
            const storedRefreshToken = getRefreshToken();
            if (!storedRefreshToken) {
              throw new Error('No refresh token stored');
            }

            // Use a raw axios call (not the intercepted instance)
            // so this call doesn't trigger the interceptor again
            const refreshResponse = await axios.post<{
              isSuccess: boolean;
              response: { access_token: string; refresh_token: string };
            }>(
              `${import.meta.env.VITE_API_BASE_URL}/refresh`,
              { refresh_token: storedRefreshToken },
              { headers: { 'Content-Type': 'application/json' } }
            );

            const { access_token, refresh_token } =
              refreshResponse.data.response;

            setAccessToken(access_token);
            setRefreshToken(refresh_token);

            this.isRefreshing = false;
            this.onRefreshed(access_token);

            originalRequest.headers['Authorization'] =
              `Bearer ${access_token}`;
            return this.axiosInstance(originalRequest);

          } catch (refreshError) {
            this.isRefreshing = false;
            this.onRefreshFailed(
              refreshError instanceof Error ? refreshError : error
            );
            clearTokens();
            this.redirectToLogin();
            return Promise.reject(toApiRequestError(error, 'Session refresh failed.'));
          }
        }

        return Promise.reject(toApiRequestError(error));
      }
    );
  }

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const res = await this.axiosInstance.get<T>(url, config);
    return res.data;
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const res = await this.axiosInstance.post<T>(url, data, config);
    return res.data;
  }

  public async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const res = await this.axiosInstance.put<T>(url, data, config);
    return res.data;
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const res = await this.axiosInstance.patch<T>(url, data, config);
    return res.data;
  }

  public async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const res = await this.axiosInstance.delete<T>(url, config);
    return res.data;
  }

  public getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Export the singleton — this is what all feature services import
export const apiService = BaseApiService.getInstance();
